import {
  getAllowedFilterOperators,
  type TableDescriptor,
  type TableColumnDescriptor,
  type TableAggregationFunction,
} from '@finance/contracts';
import type { FilterOperator } from './filters/filter-registry.ts';

export interface TableSortState {
  field: string;
  direction: 'asc' | 'desc';
}

export interface TableFilterState {
  field: string;
  operator: FilterOperator;
  value: unknown;
}

export interface TableGroupState {
  field: string;
  direction: 'asc' | 'desc';
}

export interface TableAggregationState {
  field: string;
  function: TableAggregationFunction;
}

export interface TableColumnVisibilityState {
  columnId: string;
  visible: boolean;
}

export interface TableColumnOrderState {
  columnId: string;
  order: number;
}

export interface TableColumnSizeState {
  columnId: string;
  width: number;
}

export interface TablePinnedColumnsState {
  left: string[];
  right: string[];
}

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids)];
}

/**
 * Read the user's default page size preference from localStorage.
 * Returns undefined if the preference has not been set yet (will fall back to contract defaults).
 * This value is populated by SessionProvider when preferences are loaded from the server.
 */
export function getGlobalDefaultPageSize(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const storage = window.localStorage;
  if (!storage || typeof storage.getItem !== 'function') return undefined;
  const raw = storage.getItem('finance:default-page-size');
  if (!raw) return undefined;
  const n = Number(raw);
  if (Number.isSafeInteger(n) && n > 0) return n;
  return undefined;
}

export function getAllowedTablePageSizes(descriptor: TableDescriptor): number[] {
  const candidates = [
    descriptor.pagination?.pageSize ?? DEFAULT_TABLE_STATE.pagination.pageSize,
    ...(descriptor.pagination?.pageSizeOptions ?? []),
  ];
  return [...new Set(candidates.filter(value => Number.isSafeInteger(value) && value > 0))].sort(
    (left, right) => left - right,
  );
}

export function normalizeTablePageSize(pageSize: number, descriptor: TableDescriptor): number {
  const allowed = getAllowedTablePageSizes(descriptor);
  if (allowed.includes(pageSize)) return pageSize;
  const descriptorDefault = descriptor.pagination?.pageSize;
  if (descriptorDefault && allowed.includes(descriptorDefault)) return descriptorDefault;
  return allowed[0] ?? DEFAULT_TABLE_STATE.pagination.pageSize;
}

export function normalizePinnedColumns(
  pinned: TablePinnedColumnsState,
  options: {
    allowedIds: Set<string>;
    visibleIds: Set<string>;
    preferredSide?: 'left' | 'right';
  },
): TablePinnedColumnsState {
  const preferredSide = options.preferredSide ?? 'left';
  const left = uniqueIds(pinned.left).filter(
    id => options.allowedIds.has(id) && options.visibleIds.has(id),
  );
  const rightCandidates = uniqueIds(pinned.right).filter(
    id => options.allowedIds.has(id) && options.visibleIds.has(id),
  );
  const leftSet = new Set(left);
  const right = rightCandidates.filter(id => !leftSet.has(id));

  if (preferredSide === 'right') {
    const rightSet = new Set(right);
    return { left: left.filter(id => !rightSet.has(id)), right };
  }
  return { left, right };
}

export interface TableSelectionState {
  selectedIds: string[];
  selectAllMode: 'page';
}

export interface TableState {
  sorting: TableSortState[];
  filters: TableFilterState[];
  globalSearch: string;
  grouping: TableGroupState[];
  aggregations: TableAggregationState[];
  pagination: { page: number; pageSize: number };
  columnVisibility: TableColumnVisibilityState[];
  columnOrder: TableColumnOrderState[];
  columnSizes: TableColumnSizeState[];
  pinnedColumns: TablePinnedColumnsState;
  density: 'compact' | 'standard' | 'comfortable';
  expandedGroups: string[];
  selection: TableSelectionState;
  savedViewId: string | null;
  hasUnsavedChanges: boolean;
}

export interface TableStateAction {
  type: string;
  payload?: unknown;
}

export const DEFAULT_TABLE_STATE: TableState = {
  sorting: [],
  filters: [],
  globalSearch: '',
  grouping: [],
  aggregations: [],
  pagination: { page: 0, pageSize: 25 },
  columnVisibility: [],
  columnOrder: [],
  columnSizes: [],
  pinnedColumns: { left: [], right: [] },
  density: 'standard',
  expandedGroups: [],
  selection: { selectedIds: [], selectAllMode: 'page' },
  savedViewId: null,
  hasUnsavedChanges: false,
};

export function getDefaultColumnVisibility(
  columns: TableColumnDescriptor[]
): TableColumnVisibilityState[] {
  return columns.map(col => ({
    columnId: col.id,
    visible: col.requiredVisible === true ? true : col.defaultVisible ?? true,
  }));
}

export function getDefaultColumnOrder(columns: TableColumnDescriptor[]): TableColumnOrderState[] {
  return columns.map((col, index) => ({ columnId: col.id, order: index }));
}

export function getDefaultColumnSizes(
  columns: TableColumnDescriptor[]
): TableColumnSizeState[] {
  return columns.map(col => ({ columnId: col.id, width: col.defaultWidth ?? 0 }));
}

function normalizeColumnWidth(column: TableColumnDescriptor, requested: number | undefined): number {
  const fallback = column.defaultWidth ?? 0;
  const numeric = Number.isFinite(requested) && (requested ?? 0) > 0 ? requested! : fallback;
  if (numeric <= 0) return fallback;
  return Math.max(column.minWidth ?? 1, Math.min(column.maxWidth ?? Number.MAX_SAFE_INTEGER, numeric));
}

export function createTableInitialState(
  descriptor: TableDescriptor,
  persistedState?: Partial<TableState> | null,
  overrides?: Partial<TableState>,
): TableState {
  const requestedPageSize =
    getGlobalDefaultPageSize() ?? descriptor.pagination?.pageSize ?? DEFAULT_TABLE_STATE.pagination.pageSize;
  const baseState: TableState = {
    sorting: descriptor.defaultSort
      ? [{ field: descriptor.defaultSort.field, direction: descriptor.defaultSort.direction }]
      : [],
    filters: [],
    globalSearch: '',
    grouping: [],
    aggregations: [],
    pagination: { page: 0, pageSize: normalizeTablePageSize(requestedPageSize, descriptor) },
    columnVisibility: getDefaultColumnVisibility(descriptor.columns),
    columnOrder: getDefaultColumnOrder(descriptor.columns),
    columnSizes: getDefaultColumnSizes(descriptor.columns),
    pinnedColumns: { left: [], right: [] },
    density: DEFAULT_TABLE_STATE.density,
    expandedGroups: [],
    selection: { ...DEFAULT_TABLE_STATE.selection },
    savedViewId: null,
    hasUnsavedChanges: false,
  };

  const descriptorDefaultView = descriptor.savedViews?.find(view => view.isDefault);
  const mergedState = {
    ...baseState,
    ...(descriptorDefaultView?.state ?? {}),
    ...(persistedState ?? {}),
    ...(overrides ?? {}),
  } as TableState;

  return normalizeStateAgainstDescriptor(mergedState, descriptor);
}

export function createSavedViewStatePatch(
  descriptor: TableDescriptor,
  viewState: Partial<TableState>,
  viewId?: string | null,
): Partial<TableState> {
  const normalized = normalizeStateAgainstDescriptor(
    {
      ...createTableInitialState(descriptor),
      ...viewState,
      savedViewId: viewId ?? null,
      hasUnsavedChanges: false,
    },
    descriptor,
  );

  return {
    sorting: normalized.sorting,
    filters: normalized.filters,
    globalSearch: normalized.globalSearch,
    grouping: normalized.grouping,
    aggregations: normalized.aggregations,
    pagination: { ...normalized.pagination, page: 0 },
    columnVisibility: normalized.columnVisibility,
    columnOrder: normalized.columnOrder,
    columnSizes: normalized.columnSizes,
    pinnedColumns: normalized.pinnedColumns,
    density: normalized.density,
    expandedGroups: normalized.expandedGroups,
    savedViewId: viewId ?? null,
    hasUnsavedChanges: false,
  };
}

export function getVisibleOrderedColumns(
  descriptor: TableDescriptor,
  state: Pick<TableState, 'columnVisibility' | 'columnOrder'>
): TableColumnDescriptor[] {
  const visibilityMap = new Map(state.columnVisibility.map(v => [v.columnId, v.visible]));
  const columnMap = new Map(descriptor.columns.map(c => [c.id, c]));
  return [...state.columnOrder]
    .sort((a, b) => a.order - b.order)
    .map(co => columnMap.get(co.columnId))
    .filter((c): c is TableColumnDescriptor => !!c)
    .filter(c => c.requiredVisible === true || visibilityMap.get(c.id) !== false);
}

export function getPinnedColumnOffsets(
  columns: TableColumnDescriptor[],
  pinnedLeft: string[],
  pinnedRight: string[],
  columnSizes: TableColumnSizeState[]
): Array<{ left: number; right: number }> {
  const pinnedLeftSet = new Set(pinnedLeft);
  const pinnedRightSet = new Set(pinnedRight);
  const widthMap = new Map(columnSizes.map(s => [s.columnId, s.width]));
  const result: Array<{ left: number; right: number }> = [];
  let cumulativeLeft = 0;
  for (const column of columns) {
    const width = widthMap.get(column.id) ?? column.defaultWidth ?? 0;
    if (pinnedLeftSet.has(column.id)) {
      result.push({ left: cumulativeLeft, right: 0 });
      cumulativeLeft += width;
    } else result.push({ left: 0, right: 0 });
  }
  let cumulativeRight = 0;
  for (let i = columns.length - 1; i >= 0; i--) {
    const column = columns[i];
    const width = widthMap.get(column.id) ?? column.defaultWidth ?? 0;
    if (pinnedRightSet.has(column.id)) {
      result[i] = { ...result[i], right: cumulativeRight };
      cumulativeRight += width;
    }
  }
  return result;
}

export function normalizeStateAgainstDescriptor(
  state: TableState,
  descriptor: TableDescriptor
): TableState {
  const columns = descriptor.columns;
  const validColumnIds = new Set(columns.map(c => c.id));
  const requiredColumns = new Set(columns.filter(c => c.requiredVisible).map(c => c.id));
  const priorVisibility = new Map(state.columnVisibility.map(entry => [entry.columnId, entry.visible]));
  const defaultVisibility = new Map(getDefaultColumnVisibility(columns).map(entry => [entry.columnId, entry.visible]));

  const normalizedVisibility = columns.map(column => ({
    columnId: column.id,
    visible:
      requiredColumns.has(column.id)
        ? true
        : descriptor.capabilities?.columnVisibility === false
          ? (defaultVisibility.get(column.id) ?? true)
          : (priorVisibility.get(column.id) ?? defaultVisibility.get(column.id) ?? true),
  }));
  const visibleColumnIds = new Set(
    normalizedVisibility.filter(entry => entry.visible).map(entry => entry.columnId),
  );

  const priorOrder = [...state.columnOrder]
    .sort((left, right) => left.order - right.order)
    .map(entry => entry.columnId)
    .filter(id => validColumnIds.has(id));
  const uniquePriorOrder = uniqueIds(priorOrder);
  const missingOrder = columns.map(column => column.id).filter(id => !uniquePriorOrder.includes(id));
  const orderedIds = descriptor.capabilities?.columnReordering === false
    ? columns.map(column => column.id)
    : [...uniquePriorOrder, ...missingOrder];
  const normalizedOrder = orderedIds.map((columnId, order) => ({ columnId, order }));

  const priorSizes = new Map(state.columnSizes.map(entry => [entry.columnId, entry.width]));
  const normalizedSizes = columns.map(column => ({
    columnId: column.id,
    width: descriptor.capabilities?.columnResizing === false
      ? (column.defaultWidth ?? 0)
      : normalizeColumnWidth(column, priorSizes.get(column.id)),
  }));

  const pinnableColumns = new Set(columns.filter(c => c.pinnable).map(c => c.id));
  const normalizedPinnedColumns = descriptor.capabilities?.columnPinning === false
    ? { left: [], right: [] }
    : normalizePinnedColumns(state.pinnedColumns, {
        allowedIds: pinnableColumns,
        visibleIds: visibleColumnIds,
        preferredSide: 'left',
      });

  const normalizedSorting = descriptor.capabilities?.sorting === false
    ? []
    : state.sorting.filter(sort => {
        const column = columns.find(c => c.field === sort.field || c.id === sort.field);
        return Boolean(column?.sortable && (sort.direction === 'asc' || sort.direction === 'desc'));
      });
  const normalizedFilters = descriptor.capabilities?.filtering === false
    ? []
    : state.filters.filter(filter => {
        const column = columns.find(c => c.field === filter.field || c.id === filter.field);
        return Boolean(
          column?.filterable &&
          getAllowedFilterOperators(column).includes(filter.operator as never),
        );
      });
  const normalizedGrouping = descriptor.capabilities?.grouping === false
    ? []
    : state.grouping.filter(group => {
        const column = columns.find(c => c.field === group.field || c.id === group.field);
        return Boolean(column?.groupable && (group.direction === 'asc' || group.direction === 'desc'));
      });
  const normalizedAggregations = descriptor.capabilities?.aggregation === false
    ? []
    : state.aggregations.filter(aggregation => {
        const column = columns.find(c => c.field === aggregation.field || c.id === aggregation.field);
        return column?.allowedAggregations?.includes(aggregation.function) ?? false;
      });

  return {
    ...state,
    sorting: normalizedSorting,
    filters: normalizedFilters,
    globalSearch: descriptor.capabilities?.globalSearch === false ? '' : state.globalSearch,
    grouping: normalizedGrouping,
    aggregations: normalizedAggregations,
    pagination: {
      page: Math.max(0, Number.isFinite(state.pagination.page) ? Math.floor(state.pagination.page) : 0),
      pageSize: normalizeTablePageSize(state.pagination.pageSize, descriptor),
    },
    columnVisibility: normalizedVisibility,
    columnOrder: normalizedOrder,
    columnSizes: normalizedSizes,
    pinnedColumns: normalizedPinnedColumns,
    density: descriptor.capabilities?.density === false ? 'standard' : state.density,
    expandedGroups: normalizedGrouping.length > 0 ? uniqueIds(state.expandedGroups) : [],
    selection: descriptor.capabilities?.selection === false
      ? { selectedIds: [], selectAllMode: 'page' }
      : {
          selectedIds: uniqueIds(state.selection.selectedIds),
          selectAllMode: 'page',
        },
  };
}
